import { Card, Col, Row, Typography } from 'antd';
import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ComparisonStats,
  PhlebotomistStats,
} from '../../../types/phlebotomist-dashboard';
import { DeltaIndicator } from '../../charts/DeltaIndicator';
import { PhlebTooltip } from '../../PhlebTooltip';

const { Text } = Typography;

// Color scheme for stats - light variants for dark background
const STAT_COLORS = {
  drawSuccess: '#fff', // White
  attempted: '#cedbef', // Pale blue-gray (was Warm beige #4f6d7a)
  failures: '#fca5a5', // Light red (was Brand red #f31a25)
  unitSuccess: '#fff', // White
  expected: '#a7b4c2', // Medium gray-blue (was Cool gray-blue #6e7a8a)
  actual: '#fff', // White (was Royal blue #1565c0)
};

interface IStatRowProps {
  label: ReactNode;
  value: number | string;
  color: string;
  suffix?: string;
  size?: 'normal' | 'small';
  relativePercent?: number | null; // Relative percentage compared to filtered stats
  /** Comparison value for inline mode */
  comparisonValue?: number | string;
  /** Whether higher is better for delta calculation */
  higherIsBetter?: boolean;
  /** Whether to show bottom border (table row separator) */
  showBorder?: boolean;
  /** Whether to bold the label */
  boldLabel?: boolean;
  /** Whether to use a bolder/thicker border */
  boldBorder?: boolean;
  /** Whether in compact/split mode (reduced side padding) */
  isCompactMode?: boolean;
  /** Previous period value for trend arrow (normal mode only) */
  previousPeriodValue?: number | string;
  /** Sub-label under the main label (e.g. "Derek's Success / Office Ave") */
  relativeLabel?: string;
  /** Hidden label text to reserve matching vertical space (for cross-card alignment) */
  reserveRelativeLabelText?: string;
}

const StatRow: FC<IStatRowProps> = ({
  label,
  value,
  color,
  suffix = '',
  size = 'normal',
  relativePercent,
  comparisonValue,
  higherIsBetter = true,
  showBorder = true,
  boldLabel = false,
  boldBorder = false,
  isCompactMode = false,
  previousPeriodValue,
  relativeLabel,
  reserveRelativeLabelText,
}) => {
  // Responsive font sizes - smaller in compact mode
  const fontSize = isCompactMode ? 12 : size === 'small' ? 13 : 14;
  const valueSize = isCompactMode ? 14 : size === 'small' ? 15 : 18;
  const relativeSize = isCompactMode ? 12 : size === 'small' ? 14 : 16;

  // Responsive column widths - smaller in compact mode
  const labelWidth = isCompactMode ? 55 : 85;
  const valueWidth = isCompactMode ? 50 : 70;
  const deltaWidth = isCompactMode ? 50 : 65;
  const spacerWidth = isCompactMode ? 6 : 12;
  const spacerWidthLg = isCompactMode ? 8 : 16;

  // Border style: bolder border uses 2px and lighter translucent color
  const borderStyle = boldBorder
    ? '2px solid rgba(255,255,255,0.25)'
    : '1px solid rgba(255,255,255,0.1)';

  // Determine color for relative percentage: green if >= 100%, red if < 100%
  const relativeColor =
    relativePercent !== null && relativePercent !== undefined
      ? relativePercent >= 100
        ? '#52c41a' // Green
        : '#ff9b9b' // Lighter red (visible on dark blue)
      : undefined;

  const numericValue = typeof value === 'number' ? value : parseFloat(value);
  const numericCompValue =
    typeof comparisonValue === 'number'
      ? comparisonValue
      : comparisonValue !== undefined
      ? parseFloat(comparisonValue)
      : undefined;
  const numericPrevValue =
    typeof previousPeriodValue === 'number'
      ? previousPeriodValue
      : previousPeriodValue !== undefined
      ? parseFloat(previousPeriodValue)
      : undefined;

  // Side margin calculation: minimal since parent 75% wrapper handles width
  const sideMargin = isCompactMode ? 10 : 0;

  // When in inline compare mode, use layout: Label | lg space | Current | sm space | Compare | sm space | Delta
  // Center the content with min 150px padding on each side when space allows
  // Border only extends ~10px beyond the data content
  if (comparisonValue !== undefined) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          width: '100%',
          padding: isCompactMode ? '4px 0' : '6px 0',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            maxWidth: `calc(100% - ${sideMargin}px)`,
            minWidth: isCompactMode ? 200 : 340,
            borderBottom: showBorder ? borderStyle : 'none',
            padding: `0 ${isCompactMode ? 4 : 10}px ${
              showBorder ? (isCompactMode ? 4 : 6) : 0
            }px ${isCompactMode ? 4 : 10}px`,
          }}
        >
          {/* Label column - fixed width */}
          <Text
            strong={boldLabel}
            style={{ fontSize, color: '#cedbef', flex: `0 0 ${labelWidth}px` }}
          >
            {label}
          </Text>
          {/* Large spacer after label */}
          <div style={{ flex: 1 }} />
          {/* Current value column - right aligned, fixed width */}
          <Text
            strong
            style={{
              fontSize: valueSize,
              color,
              flex: `0 0 ${valueWidth}px`,
              textAlign: 'right',
            }}
          >
            {value}
            {suffix}
          </Text>
          {/* Small spacer */}
          <div style={{ flex: `0 0 ${spacerWidthLg}px` }} />
          {/* Compare value column - right aligned, fixed width */}
          <Text
            style={{
              fontSize: relativeSize,
              color: '#dce0e4',
              flex: `0 0 ${valueWidth}px`,
              textAlign: 'right',
            }}
          >
            {comparisonValue}
            {suffix}
          </Text>
          {/* Small spacer */}
          <div style={{ flex: `0 0 ${spacerWidth}px` }} />
          {/* Delta column - right aligned, fixed width */}
          <div
            style={{
              flex: `0 0 ${deltaWidth}px`,
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            {numericCompValue !== undefined && !isNaN(numericValue) && (
              <DeltaIndicator
                current={numericValue}
                comparison={numericCompValue}
                higherIsBetter={higherIsBetter}
                size="small"
                darkBackground
                isPercentage={suffix.includes('%')}
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  // Normal mode (no inline compare) - table-like layout: Label | Trend | Value
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
        padding: isCompactMode ? '4px 0' : '6px 0',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: isCompactMode ? 4 : 8,
          width: '100%',
          maxWidth: `calc(100% - ${sideMargin}px)`,
          minWidth: isCompactMode ? 140 : 180,
          borderBottom: showBorder ? borderStyle : 'none',
          padding: `0 ${isCompactMode ? 4 : 10}px ${
            showBorder ? (isCompactMode ? 4 : 6) : 0
          }px ${isCompactMode ? 4 : 10}px`,
        }}
      >
        {/* Label column - flexible, takes remaining space */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text
            strong={boldLabel}
            style={{ fontSize, color: '#cedbef', display: 'block' }}
          >
            {label}
          </Text>
          {relativeLabel ? (
            <Text
              style={{
                fontSize: isCompactMode ? 9 : 10,
                color: 'rgba(255,255,255,0.5)',
                display: 'block',
                marginTop: -1,
              }}
            >
              {relativeLabel}
            </Text>
          ) : (
            reserveRelativeLabelText && (
              <Text
                aria-hidden
                style={{
                  fontSize: isCompactMode ? 9 : 10,
                  display: 'block',
                  marginTop: -1,
                  visibility: 'hidden',
                }}
              >
                {reserveRelativeLabelText}
              </Text>
            )
          )}
        </div>
        {/* Trend indicator column - fixed width, centered */}
        <div
          style={{
            flex: `0 0 ${deltaWidth}px`,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          {numericPrevValue !== undefined && !isNaN(numericValue) && (
            <DeltaIndicator
              current={numericValue}
              comparison={numericPrevValue}
              higherIsBetter={higherIsBetter}
              size="small"
              showZero
              darkBackground
              isPercentage={suffix.includes('%')}
            />
          )}
        </div>
        {/* Value column - right aligned, stacked with relative percent below */}
        <div
          style={{
            flex: `0 0 ${valueWidth}px`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
          }}
        >
          <Text
            strong
            style={{
              fontSize: valueSize,
              color,
              textAlign: 'right',
            }}
          >
            {value}
            {suffix}
          </Text>
          {/* Relative percent (when available) - stacked below main value */}
          {relativePercent !== null && relativePercent !== undefined && (
            <Text
              strong
              style={{
                fontSize: isCompactMode ? 11 : 13,
                color: relativeColor,
                textAlign: 'right',
                marginTop: -2,
              }}
            >
              {relativePercent.toFixed(1)}%
            </Text>
          )}
        </div>
      </div>
    </div>
  );
};

interface IComparisonCardProps {
  stats: ComparisonStats;
  highlight?: boolean;
  filteredStats?: ComparisonStats | null; // The filtered/your stats to compare against
  /** Inline comparison stats (for inline compare mode) */
  inlineComparisonStats?: ComparisonStats | null;
  /** Current date range label for column header (inline mode) */
  currentDateRangeLabel?: string;
  /** Comparison date range label for column header (inline mode) */
  comparisonDateRangeLabel?: string;
  /** Whether in compact/split mode (reduced side padding) */
  isCompactMode?: boolean;
  /** Whether in split mode - always show pending row even if 0 */
  isSplitMode?: boolean;
  /** Previous period stats for trend arrows (normal mode only) */
  previousPeriodStats?: ComparisonStats | null;
  /** Label for relative percent comparison (e.g. "Derek's Success / Office Ave") */
  relativeLabel?: string;
  /** Hidden label text to reserve matching vertical space (for cross-card alignment) */
  reserveRelativeLabelText?: string;
}

// Calculate relative percentage: (filteredValue / cardValue) * 100
// Returns null if cardValue is 0 or if this is the filtered stats card itself
const calculateRelativePercent = (
  filteredValue: number,
  cardValue: number,
  isFilteredCard: boolean
): number | null => {
  if (isFilteredCard || cardValue === 0) return null;
  return (filteredValue / cardValue) * 100;
};

const ComparisonCard: FC<IComparisonCardProps> = ({
  stats,
  highlight = false,
  filteredStats,
  inlineComparisonStats,
  currentDateRangeLabel,
  comparisonDateRangeLabel,
  isCompactMode = false,
  isSplitMode = false,
  previousPeriodStats: prevStats,
  relativeLabel,
  reserveRelativeLabelText,
}) => {
  const { t } = useTranslation('dashboard.PhlebotomistDashboard');

  // Calculate relative percentages for draw and unit success rates
  const drawRelativePercent = calculateRelativePercent(
    filteredStats?.overall.drawSuccessRate ?? 0,
    stats.overall.drawSuccessRate,
    highlight // highlight means this IS the filtered stats card
  );

  const unitRelativePercent = calculateRelativePercent(
    filteredStats?.overall.unitSuccessRate ?? 0,
    stats.overall.unitSuccessRate,
    highlight
  );

  const prbcUnitRelativePercent = calculateRelativePercent(
    filteredStats?.prbc.unitSuccessRate ?? 0,
    stats.prbc.unitSuccessRate,
    highlight
  );

  const ffpUnitRelativePercent = calculateRelativePercent(
    filteredStats?.ffp.unitSuccessRate ?? 0,
    stats.ffp.unitSuccessRate,
    highlight
  );

  // Inline comparison values
  const compStats = inlineComparisonStats;

  // Compare mode: cards shown side-by-side with relative labels
  const isCompareMode = !!(relativeLabel || reserveRelativeLabelText);

  // Side margin for column headers - minimal since parent 75% wrapper handles width
  const sideMargin = isCompactMode ? 10 : 0;

  return (
    <Card
      size="small"
      style={{
        height: '100%',
        background: '#1565c0',
        border: highlight ? '2px solid #60a5fa' : '1px solid #0b345f',
        boxShadow: highlight ? '0 0 8px rgba(96, 165, 250, 0.3)' : undefined,
        borderRadius: 6,
      }}
    >
      <div style={{ width: '100%' }}>
        {/* Card label */}
        {stats.label && (
          <Text
            strong
            style={{
              display: 'block',
              textAlign: 'center',
              fontSize: 15,
              color: '#fff',
              marginBottom: 6,
              letterSpacing: 0.3,
            }}
          >
            {stats.label}
          </Text>
        )}

        {/* Pending donations notice */}
        {((stats.overall.pendingDonations ?? 0) > 0 ||
          isSplitMode ||
          isCompareMode) && (
          <Text
            style={{
              display: 'block',
              textAlign: 'center',
              fontSize: 12,
              marginTop: 4,
              marginBottom: 4,
              fontStyle: 'italic',
              color: '#eaf3fc',
              visibility:
                (stats.overall.pendingDonations ?? 0) > 0 || isSplitMode
                  ? 'visible'
                  : 'hidden',
            }}
          >
            {isCompareMode ? '' : '-- '}
            {stats.overall.pendingDonations ?? 0} Donation
            {(stats.overall.pendingDonations ?? 0) !== 1 ? 's' : ''} pending
            processing are not included
            {isCompareMode ? '' : ' --'}
          </Text>
        )}

        {/* Section header: Overall */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: 6,
            marginTop: 4,
          }}
        >
          <div
            style={{
              flex: 1,
              height: 1,
              backgroundColor: 'rgba(255,255,255,0.2)',
              marginRight: 12,
            }}
          />
          <Text style={{ fontSize: 16, fontWeight: 500, color: '#cedbef' }}>
            {t('cards.comparison.overall', 'Overall')}
          </Text>
          <div
            style={{
              flex: 1,
              height: 1,
              backgroundColor: 'rgba(255,255,255,0.2)',
              marginLeft: 12,
            }}
          />
        </div>

        {/* Column headers for inline compare mode - below Overall header */}
        {compStats && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              width: '100%',
              padding: '2px 0',
              marginBottom: 4,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                maxWidth: `calc(100% - ${sideMargin}px)`,
                minWidth: 340, // Minimum content width including padding
                padding: '0 10px', // Match row padding
              }}
            >
              {/* Empty label column */}
              <div style={{ flex: '0 0 85px' }} />
              {/* Large spacer after label */}
              <div style={{ flex: 1 }} />
              {/* Current header - right aligned, fixed width */}
              <Text
                strong
                style={{
                  flex: '0 0 70px',
                  textAlign: 'right',
                  fontSize: 11,
                  color: '#fff',
                }}
              >
                {currentDateRangeLabel || t('columnHeaders.current', 'Current')}
              </Text>
              {/* Small spacer */}
              <div style={{ flex: '0 0 16px' }} />
              {/* Comparison header - right aligned, fixed width */}
              <Text
                style={{
                  flex: '0 0 70px',
                  textAlign: 'right',
                  fontSize: 11,
                  color: '#dce0e4',
                }}
              >
                {comparisonDateRangeLabel ||
                  t('columnHeaders.comparison', 'Comp')}
              </Text>
              {/* Small spacer */}
              <div style={{ flex: '0 0 12px' }} />
              {/* Change header - right aligned, fixed width */}
              <Text
                style={{
                  flex: '0 0 65px',
                  textAlign: 'right',
                  fontSize: 11,
                  color: '#dce0e4',
                }}
              >
                {t('columnHeaders.change', 'Change')}
              </Text>
            </div>
          </div>
        )}

        <StatRow
          label={
            <PhlebTooltip text="Percentage of attempted donations where at least one product unit was successfully collected (not discarded). Draw Success = Successful Donations / Attempted Donations.">
              {t('cards.comparison.drawSuccess', 'Draw Success')}
            </PhlebTooltip>
          }
          value={stats.overall.drawSuccessRate}
          suffix="%"
          color={STAT_COLORS.drawSuccess}
          relativePercent={compStats ? undefined : drawRelativePercent}
          relativeLabel={
            !compStats && drawRelativePercent != null
              ? relativeLabel
              : undefined
          }
          reserveRelativeLabelText={reserveRelativeLabelText}
          comparisonValue={compStats?.overall.drawSuccessRate}
          previousPeriodValue={
            !compStats ? prevStats?.overall.drawSuccessRate : undefined
          }
          higherIsBetter
          boldLabel
          isCompactMode={isCompactMode}
        />
        <StatRow
          label={
            <PhlebTooltip text="Total number of donation draws attempted during the period. Includes all outcomes: successful, partial, failed, and pending.">
              {t('cards.comparison.attempted', 'Attempted')}
            </PhlebTooltip>
          }
          value={stats.overall.attempted}
          color={STAT_COLORS.attempted}
          comparisonValue={compStats?.overall.attempted}
          previousPeriodValue={
            !compStats ? prevStats?.overall.attempted : undefined
          }
          higherIsBetter
          isCompactMode={isCompactMode}
        />
        {((stats.overall.pendingDonations ?? 0) > 0 || isSplitMode) && (
          <StatRow
            label={t(
              'cards.comparison.pendingProcessing',
              'Pending Processing'
            )}
            value={stats.overall.pendingDonations ?? 0}
            color="#faad14"
            comparisonValue={compStats?.overall.pendingDonations}
            previousPeriodValue={
              !compStats ? prevStats?.overall.pendingDonations : undefined
            }
            higherIsBetter={false}
            isCompactMode={isCompactMode}
          />
        )}
        <StatRow
          label={
            <PhlebTooltip text="Number of donations where all product units were discarded (completely failed). Does not include partially failed donations where some units were recovered.">
              {t('cards.comparison.failures', 'Failures')}
            </PhlebTooltip>
          }
          value={stats.overall.failures}
          color={STAT_COLORS.failures}
          comparisonValue={compStats?.overall.failures}
          previousPeriodValue={
            !compStats ? prevStats?.overall.failures : undefined
          }
          higherIsBetter={false}
          boldBorder
          isCompactMode={isCompactMode}
        />
        <StatRow
          label={
            <PhlebTooltip text="Percentage of expected product units that were actually collected. Unit Success = Actual Units / Expected Units. Measures how efficiently donations convert to usable products.">
              {t('cards.comparison.unitSuccess', 'Unit Success')}
            </PhlebTooltip>
          }
          value={stats.overall.unitSuccessRate}
          suffix="%"
          color={STAT_COLORS.unitSuccess}
          relativePercent={compStats ? undefined : unitRelativePercent}
          relativeLabel={
            !compStats && unitRelativePercent != null
              ? relativeLabel
              : undefined
          }
          reserveRelativeLabelText={reserveRelativeLabelText}
          comparisonValue={compStats?.overall.unitSuccessRate}
          previousPeriodValue={
            !compStats ? prevStats?.overall.unitSuccessRate : undefined
          }
          higherIsBetter
          boldLabel
          isCompactMode={isCompactMode}
        />
        <StatRow
          label={
            <PhlebTooltip text="Total product units expected based on donor type: SGL donors = 1 unit per product type, DBL donors = 2 units per product type. Sum of all pRBC and FFP expected units.">
              {t('cards.comparison.expected', 'Expected')}
            </PhlebTooltip>
          }
          value={stats.overall.expected}
          color={STAT_COLORS.expected}
          comparisonValue={compStats?.overall.expected}
          previousPeriodValue={
            !compStats ? prevStats?.overall.expected : undefined
          }
          higherIsBetter
          isCompactMode={isCompactMode}
        />
        <StatRow
          label={
            <PhlebTooltip text="Total product units actually collected and not discarded. Sum of all pRBC and FFP actual units.">
              {t('cards.comparison.actual', 'Actual')}
            </PhlebTooltip>
          }
          value={stats.overall.actual}
          color={STAT_COLORS.actual}
          comparisonValue={compStats?.overall.actual}
          previousPeriodValue={
            !compStats ? prevStats?.overall.actual : undefined
          }
          higherIsBetter
          showBorder={false}
          isCompactMode={isCompactMode}
        />

        {/* Section header: pRBC Only */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: 6,
            marginTop: 10,
          }}
        >
          <div
            style={{
              flex: 1,
              height: 1,
              backgroundColor: 'rgba(255,255,255,0.2)',
              marginRight: 12,
            }}
          />
          <Text style={{ fontSize: 16, fontWeight: 500, color: '#cedbef' }}>
            {t('cards.comparison.prbcOnly', 'pRBC Only')}
          </Text>
          <div
            style={{
              flex: 1,
              height: 1,
              backgroundColor: 'rgba(255,255,255,0.2)',
              marginLeft: 12,
            }}
          />
        </div>

        <StatRow
          label={
            <PhlebTooltip text="pRBC unit success rate: Actual pRBC units collected / Expected pRBC units. SGL donors expect 1 pRBC unit, DBL donors expect 2.">
              {t('cards.comparison.unitSuccess', 'Unit Success')}
            </PhlebTooltip>
          }
          value={stats.prbc.unitSuccessRate}
          suffix="%"
          color={STAT_COLORS.unitSuccess}
          size="small"
          relativePercent={compStats ? undefined : prbcUnitRelativePercent}
          relativeLabel={
            !compStats && prbcUnitRelativePercent != null
              ? relativeLabel
              : undefined
          }
          reserveRelativeLabelText={reserveRelativeLabelText}
          comparisonValue={compStats?.prbc.unitSuccessRate}
          previousPeriodValue={
            !compStats ? prevStats?.prbc.unitSuccessRate : undefined
          }
          higherIsBetter
          boldLabel
          isCompactMode={isCompactMode}
        />
        <StatRow
          label={
            <PhlebTooltip text="Expected pRBC units based on donor type. SGL donors = 1 pRBC unit expected, DBL donors = 2 pRBC units expected.">
              {t('cards.comparison.expected', 'Expected')}
            </PhlebTooltip>
          }
          value={stats.prbc.expected}
          color={STAT_COLORS.expected}
          size="small"
          comparisonValue={compStats?.prbc.expected}
          previousPeriodValue={
            !compStats ? prevStats?.prbc.expected : undefined
          }
          higherIsBetter
          isCompactMode={isCompactMode}
        />
        <StatRow
          label={
            <PhlebTooltip text="Actual pRBC units collected and not discarded during the period.">
              {t('cards.comparison.actual', 'Actual')}
            </PhlebTooltip>
          }
          value={stats.prbc.actual}
          color={STAT_COLORS.actual}
          size="small"
          comparisonValue={compStats?.prbc.actual}
          previousPeriodValue={!compStats ? prevStats?.prbc.actual : undefined}
          higherIsBetter
          showBorder={false}
          isCompactMode={isCompactMode}
        />

        {/* Section header: FFP Only */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: 6,
            marginTop: 10,
          }}
        >
          <div
            style={{
              flex: 1,
              height: 1,
              backgroundColor: 'rgba(255,255,255,0.2)',
              marginRight: 12,
            }}
          />
          <Text style={{ fontSize: 16, fontWeight: 500, color: '#cedbef' }}>
            {t('cards.comparison.ffpOnly', 'FFP Only')}
          </Text>
          <div
            style={{
              flex: 1,
              height: 1,
              backgroundColor: 'rgba(255,255,255,0.2)',
              marginLeft: 12,
            }}
          />
        </div>

        <StatRow
          label={
            <PhlebTooltip text="FFP unit success rate: Actual FFP units collected / Expected FFP units. SGL donors expect 1 FFP unit, DBL donors expect 2.">
              {t('cards.comparison.unitSuccess', 'Unit Success')}
            </PhlebTooltip>
          }
          value={stats.ffp.unitSuccessRate}
          suffix="%"
          color={STAT_COLORS.unitSuccess}
          size="small"
          relativePercent={compStats ? undefined : ffpUnitRelativePercent}
          relativeLabel={
            !compStats && ffpUnitRelativePercent != null
              ? relativeLabel
              : undefined
          }
          reserveRelativeLabelText={reserveRelativeLabelText}
          comparisonValue={compStats?.ffp.unitSuccessRate}
          previousPeriodValue={
            !compStats ? prevStats?.ffp.unitSuccessRate : undefined
          }
          higherIsBetter
          boldLabel
          isCompactMode={isCompactMode}
        />
        <StatRow
          label={
            <PhlebTooltip text="Expected FFP units based on donor type. SGL donors = 1 FFP unit expected, DBL donors = 2 FFP units expected.">
              {t('cards.comparison.expected', 'Expected')}
            </PhlebTooltip>
          }
          value={stats.ffp.expected}
          color={STAT_COLORS.expected}
          size="small"
          comparisonValue={compStats?.ffp.expected}
          previousPeriodValue={!compStats ? prevStats?.ffp.expected : undefined}
          higherIsBetter
          isCompactMode={isCompactMode}
        />
        <StatRow
          label={
            <PhlebTooltip text="Actual FFP units collected and not discarded during the period.">
              {t('cards.comparison.actual', 'Actual')}
            </PhlebTooltip>
          }
          value={stats.ffp.actual}
          color={STAT_COLORS.actual}
          size="small"
          comparisonValue={compStats?.ffp.actual}
          previousPeriodValue={!compStats ? prevStats?.ffp.actual : undefined}
          higherIsBetter
          showBorder={false}
          isCompactMode={isCompactMode}
        />
      </div>
    </Card>
  );
};

interface IComparisonMetricsProps {
  yourStats: PhlebotomistStats | null;
  officeStats: ComparisonStats | null;
  regionStats?: ComparisonStats | null;
  companyStats?: ComparisonStats | null;
  showRegion: boolean;
  showCompany: boolean;
  loading?: boolean;
  canSelectPhlebotomist?: boolean;
  /** Whether a specific phlebotomist is selected (not "All") */
  isSpecificPhlebotomistSelected?: boolean;
  /** Phlebotomist name for title (admin/MD viewing specific phlebotomist) */
  phlebotomistName?: string;
  /** Measured pane width from parent - used for responsive calculations */
  paneWidth?: number;
  /** Inline comparison stats (for inline compare mode) */
  comparisonYourStats?: PhlebotomistStats | null;
  comparisonOfficeStats?: ComparisonStats | null;
  comparisonRegionStats?: ComparisonStats | null;
  comparisonCompanyStats?: ComparisonStats | null;
  /** Current date range label for column headers (inline mode) */
  currentDateRangeLabel?: string;
  /** Comparison date range label for column headers (inline mode) */
  comparisonDateRangeLabel?: string;
  /** Whether in split mode (reduced side padding) */
  isSplitMode?: boolean;
  /** Previous period stats for trend arrows (normal mode only) */
  previousPeriodStats?: PhlebotomistStats | null;
}

export const ComparisonMetrics: FC<IComparisonMetricsProps> = ({
  yourStats,
  officeStats,
  regionStats,
  companyStats,
  showRegion,
  showCompany,
  loading = false,
  canSelectPhlebotomist = false,
  isSpecificPhlebotomistSelected = false,
  phlebotomistName,
  paneWidth,
  comparisonYourStats,
  comparisonOfficeStats,
  comparisonRegionStats,
  comparisonCompanyStats,
  currentDateRangeLabel,
  comparisonDateRangeLabel,
  isSplitMode = false,
  previousPeriodStats,
}) => {
  const { t } = useTranslation('dashboard.PhlebotomistDashboard');

  // Determine if we're in inline compare mode
  const hasInlineComparison = !!(
    comparisonYourStats ||
    comparisonOfficeStats ||
    comparisonRegionStats ||
    comparisonCompanyStats
  );

  // Enable compact mode when:
  // 1. In split mode (isSplitMode)
  // 2. OR pane width is narrow (< 600px)
  const isCompactMode =
    isSplitMode || (paneWidth !== undefined && paneWidth < 600);

  // Calculate how many cards will be shown
  const cardCount =
    (yourStats ? 1 : 0) +
    (officeStats ? 1 : 0) +
    (regionStats && showRegion ? 1 : 0) +
    (companyStats && showCompany ? 1 : 0);

  // Determine layout based on pane width:
  // - Single column (1x4/1x3/1x2): pane width < 700px, OR inline mode < 900px
  // - 2x2 grid: pane width < 1200px, OR inline mode < 1400px, OR 3+ cards
  // - Full row: wide enough for all cards in one row
  const isSingleColumn =
    paneWidth !== undefined
      ? paneWidth < 700 || (hasInlineComparison && paneWidth < 900)
      : true; // Default to single column if paneWidth unknown

  const isCompact =
    paneWidth !== undefined
      ? paneWidth < 1200 ||
        (hasInlineComparison && paneWidth < 1400) ||
        cardCount > 2
      : cardCount > 2;

  // Use "{Name}'s Stats" for Admin/MD viewing a specific phlebotomist
  // Use "Your Stats" for CT users viewing their own stats
  // Use "Filtered Stats" for Admin/MD when viewing aggregated stats (All phlebotomists)
  const statsLabel = phlebotomistName
    ? `${phlebotomistName}'s Stats`
    : isSpecificPhlebotomistSelected || !canSelectPhlebotomist
    ? t('cards.comparison.yourStats', 'Your Stats')
    : t('cards.comparison.filteredStats', 'Filtered Stats');

  // Convert yourStats to ComparisonStats format
  // Exclude pending donations from draw success rate and failures
  const yourEligible = yourStats
    ? yourStats.attemptedDonations - (yourStats.pendingDonations ?? 0)
    : 0;
  const yourComparisonStats: ComparisonStats | null = yourStats
    ? {
        label: statsLabel,
        overall: {
          drawSuccessRate:
            yourEligible > 0
              ? Math.round(
                  (yourStats.successfulDonations / yourEligible) * 1000
                ) / 10
              : 0,
          attempted: yourStats.attemptedDonations,
          pendingDonations: yourStats.pendingDonations ?? 0,
          failures: yourEligible - yourStats.successfulDonations,
          unitSuccessRate:
            yourStats.expectedTotal > 0
              ? Math.round(
                  (yourStats.actualTotal / yourStats.expectedTotal) * 1000
                ) / 10
              : 0,
          expected: yourStats.expectedTotal,
          actual: yourStats.actualTotal,
        },
        prbc: {
          unitSuccessRate: yourStats.prbcSuccessRate,
          expected: yourStats.expectedPRBC,
          actual: yourStats.actualPRBC,
        },
        ffp: {
          unitSuccessRate: (() => {
            const expectedFFP =
              yourStats.expectedFFPSingle + yourStats.expectedFFPDouble;
            const actualFFP =
              yourStats.actualFFPSingle + yourStats.actualFFPDouble;
            return expectedFFP > 0
              ? Math.round((actualFFP / expectedFFP) * 1000) / 10
              : 0;
          })(),
          expected: yourStats.expectedFFPSingle + yourStats.expectedFFPDouble,
          actual: yourStats.actualFFPSingle + yourStats.actualFFPDouble,
        },
      }
    : null;

  // Convert comparison yourStats to ComparisonStats format for inline mode
  // Exclude pending donations from draw success rate and failures
  const compYourEligible = comparisonYourStats
    ? comparisonYourStats.attemptedDonations -
      (comparisonYourStats.pendingDonations ?? 0)
    : 0;
  const compYourComparisonStats: ComparisonStats | null = comparisonYourStats
    ? {
        label: statsLabel,
        overall: {
          drawSuccessRate:
            compYourEligible > 0
              ? Math.round(
                  (comparisonYourStats.successfulDonations / compYourEligible) *
                    1000
                ) / 10
              : 0,
          attempted: comparisonYourStats.attemptedDonations,
          pendingDonations: comparisonYourStats.pendingDonations ?? 0,
          failures: compYourEligible - comparisonYourStats.successfulDonations,
          unitSuccessRate:
            comparisonYourStats.expectedTotal > 0
              ? Math.round(
                  (comparisonYourStats.actualTotal /
                    comparisonYourStats.expectedTotal) *
                    1000
                ) / 10
              : 0,
          expected: comparisonYourStats.expectedTotal,
          actual: comparisonYourStats.actualTotal,
        },
        prbc: {
          unitSuccessRate: comparisonYourStats.prbcSuccessRate,
          expected: comparisonYourStats.expectedPRBC,
          actual: comparisonYourStats.actualPRBC,
        },
        ffp: {
          unitSuccessRate: (() => {
            const expectedFFP =
              comparisonYourStats.expectedFFPSingle +
              comparisonYourStats.expectedFFPDouble;
            const actualFFP =
              comparisonYourStats.actualFFPSingle +
              comparisonYourStats.actualFFPDouble;
            return expectedFFP > 0
              ? Math.round((actualFFP / expectedFFP) * 1000) / 10
              : 0;
          })(),
          expected:
            comparisonYourStats.expectedFFPSingle +
            comparisonYourStats.expectedFFPDouble,
          actual:
            comparisonYourStats.actualFFPSingle +
            comparisonYourStats.actualFFPDouble,
        },
      }
    : null;

  // Convert previousPeriodStats to ComparisonStats format for trend arrows
  const prevEligible = previousPeriodStats
    ? previousPeriodStats.attemptedDonations -
      (previousPeriodStats.pendingDonations ?? 0)
    : 0;
  const prevComparisonStats: ComparisonStats | null = previousPeriodStats
    ? {
        label: '',
        overall: {
          drawSuccessRate:
            prevEligible > 0
              ? Math.round(
                  (previousPeriodStats.successfulDonations / prevEligible) *
                    1000
                ) / 10
              : 0,
          attempted: previousPeriodStats.attemptedDonations,
          pendingDonations: previousPeriodStats.pendingDonations ?? 0,
          failures: prevEligible - previousPeriodStats.successfulDonations,
          unitSuccessRate:
            previousPeriodStats.expectedTotal > 0
              ? Math.round(
                  (previousPeriodStats.actualTotal /
                    previousPeriodStats.expectedTotal) *
                    1000
                ) / 10
              : 0,
          expected: previousPeriodStats.expectedTotal,
          actual: previousPeriodStats.actualTotal,
        },
        prbc: {
          unitSuccessRate: previousPeriodStats.prbcSuccessRate,
          expected: previousPeriodStats.expectedPRBC,
          actual: previousPeriodStats.actualPRBC,
        },
        ffp: {
          unitSuccessRate: (() => {
            const expectedFFP =
              previousPeriodStats.expectedFFPSingle +
              previousPeriodStats.expectedFFPDouble;
            const actualFFP =
              previousPeriodStats.actualFFPSingle +
              previousPeriodStats.actualFFPDouble;
            return expectedFFP > 0
              ? Math.round((actualFFP / expectedFFP) * 1000) / 10
              : 0;
          })(),
          expected:
            previousPeriodStats.expectedFFPSingle +
            previousPeriodStats.expectedFFPDouble,
          actual:
            previousPeriodStats.actualFFPSingle +
            previousPeriodStats.actualFFPDouble,
        },
      }
    : null;

  // Extract phlebotomist first name for relative label
  const phlebFirstName = phlebotomistName
    ? phlebotomistName.split(' ')[0]
    : undefined;

  // Build relative label for each comparison card type
  // Format: "{First Name}'s Success / {comparison type}"
  const buildRelativeLabel = (
    compLabel: string | undefined
  ): string | undefined => {
    if (!phlebFirstName || !compLabel) return undefined;
    // Strip parenthetical like "(Pittsburgh Home)" → "Office Avg"
    const shortLabel = compLabel.replace(/\s*\([^)]*\)\s*/g, ' ').trim();
    return `${phlebFirstName}'s Success / ${shortLabel}`;
  };

  // Find the longest relative label from sibling cards for alignment spacing
  const longestRelativeLabel = [
    buildRelativeLabel(officeStats?.label),
    showRegion ? buildRelativeLabel(regionStats?.label) : undefined,
    showCompany ? buildRelativeLabel(companyStats?.label) : undefined,
  ].reduce<string | undefined>(
    (longest, label) =>
      label && (!longest || label.length > longest.length) ? label : longest,
    undefined
  );

  // Calculate column span:
  // - 1 card or fewer: always span 24 (fill the container)
  // - Single column: span 24 (1 card per row)
  // - Compact mode: use 2x2 grid (span 12 = 2 per row)
  // - Otherwise: each card spans 24/cardCount to fill the row evenly
  const colSpan =
    cardCount <= 1
      ? 24
      : isSingleColumn
      ? 24
      : isCompact
      ? 12
      : Math.floor(24 / cardCount);

  return (
    <Card
      title={t('cards.comparison.title', 'Comparison Metrics')}
      loading={loading}
      style={{ background: '#0e4d8f', borderRadius: 8 }}
      headStyle={{
        fontSize: 18,
        color: '#fff',
        borderBottom: '1px solid #0b345f',
      }}
      bodyStyle={{ padding: 12 }}
    >
      <Row gutter={[12, 12]}>
        {yourComparisonStats && (
          <Col span={colSpan}>
            <ComparisonCard
              stats={yourComparisonStats}
              highlight
              inlineComparisonStats={compYourComparisonStats}
              currentDateRangeLabel={currentDateRangeLabel}
              comparisonDateRangeLabel={comparisonDateRangeLabel}
              isCompactMode={isCompactMode}
              isSplitMode={isSplitMode}
              previousPeriodStats={prevComparisonStats}
              reserveRelativeLabelText={longestRelativeLabel}
            />
          </Col>
        )}

        {officeStats && (
          <Col span={colSpan}>
            <ComparisonCard
              stats={officeStats}
              filteredStats={yourComparisonStats}
              inlineComparisonStats={comparisonOfficeStats}
              currentDateRangeLabel={currentDateRangeLabel}
              comparisonDateRangeLabel={comparisonDateRangeLabel}
              isCompactMode={isCompactMode}
              isSplitMode={isSplitMode}
              relativeLabel={buildRelativeLabel(officeStats.label)}
            />
          </Col>
        )}

        {showRegion && regionStats && (
          <Col span={colSpan}>
            <ComparisonCard
              stats={regionStats}
              filteredStats={yourComparisonStats}
              inlineComparisonStats={comparisonRegionStats}
              currentDateRangeLabel={currentDateRangeLabel}
              comparisonDateRangeLabel={comparisonDateRangeLabel}
              isCompactMode={isCompactMode}
              isSplitMode={isSplitMode}
              relativeLabel={buildRelativeLabel(regionStats.label)}
            />
          </Col>
        )}

        {showCompany && companyStats && (
          <Col span={colSpan}>
            <ComparisonCard
              stats={companyStats}
              filteredStats={yourComparisonStats}
              inlineComparisonStats={comparisonCompanyStats}
              currentDateRangeLabel={currentDateRangeLabel}
              comparisonDateRangeLabel={comparisonDateRangeLabel}
              isCompactMode={isCompactMode}
              isSplitMode={isSplitMode}
              relativeLabel={buildRelativeLabel(companyStats.label)}
            />
          </Col>
        )}
      </Row>
    </Card>
  );
};

export default ComparisonMetrics;
